import React from 'react'

import { CountryCode, allCountries, isCountryCode } from '@common'
import { skipToken } from '@reduxjs/toolkit/query'
import { useTranslations } from 'next-intl'
import Head from 'next/head'

import { Page } from '@/components/Page'
import { MyGamesContainer } from '@/features/account/MyGames'
import { PaymentInfoContainer } from '@/features/account/PaymentInfo'
import { PurchasesContainer } from '@/features/account/Purchases'
import { Game, games as allGames } from '@/fixtures/games'
import { wrapGetServerSideProps } from '@/ssr/wrapper'
import { useAuthenticatedOnly } from '@/utils/useAuthenticatedOnly'

import { config } from '../../config'
import { useFetchStoredPaymentMethodsQuery } from '../api'
import { BillingAddress } from '../features/account/BillingAddress'
import { SCIDContainer } from '../features/account/SCID'
import { LOGIN_STATUS } from '../features/user/userSlice'
import { adminService } from '../services/AdminService'
import { getAccountId } from '../utils/getAccountId'
import { useAppSelector } from '../utils/useAppSelector'

import styles from './account.module.scss'

export default function Account({ countryCodes, games }: { countryCodes: CountryCode[]; games: Game[] }) {
  const isLoggedIn = useAppSelector((state) => state.user.loginStatus === LOGIN_STATUS.LOGGED_IN)
  const isUninitialized = useAppSelector((state) => state.user.loginStatus === LOGIN_STATUS.SSO_LOGIN_MIGHT_HAPPEN)
  const paymentProvider = useAppSelector((state) => state.config.market?.paymentProvider)
  const shouldSkip = !isLoggedIn ? skipToken : undefined
  const { data: storedPaymentMethods, refetch } = useFetchStoredPaymentMethodsQuery(shouldSkip)
  const showPaymentInfo = paymentProvider === 'XSOLLA' ? storedPaymentMethods && storedPaymentMethods?.length > 0 : true
  const profile = useAppSelector((state) => state.user?.profile)
  const t = useTranslations()
  const { isPromptingLogin } = useAuthenticatedOnly({ login: false, redirectPath: '/', isLoading: isUninitialized })

  const isLoading = (!isLoggedIn && !profile) || isPromptingLogin || isUninitialized

  return (
    <Page dataCy="account-page" className={styles.Page}>
      <Head>
        <title>{t('route_account')}</title>
      </Head>
      <div className={styles.Content}>
        <h2 className={styles.PageTitle}>{t('acc_tit')}</h2>
        {!isLoading && (
          <>
            <SCIDContainer />
            {profile?.isCreator && <BillingAddress countryCodes={countryCodes} />}
            <MyGamesContainer games={games} />
            <PurchasesContainer />
            {showPaymentInfo && (
              <PaymentInfoContainer storedPaymentMethods={storedPaymentMethods} paymentMethodRemoved={refetch} />
            )}
          </>
        )}
      </div>
    </Page>
  )
}

export const getServerSideProps = wrapGetServerSideProps(async (store, context) => {
  const viewerCountry = store.getState().config.viewerCountry
  const accountId = getAccountId(context)
  const isTestAccount = Boolean(accountId && (await adminService.getIsTestAccount(accountId)))
  const isSquadEnabled = (viewerCountry && config.SQUAD_ENABLED_COUNTRIES.includes(viewerCountry)) || isTestAccount

  const games = isSquadEnabled ? allGames : allGames.filter((game) => game.slug !== 'squadbusters')
  return {
    props: {
      countryCodes: Object.entries(allCountries)
        .map(([value]) => value)
        .filter(isCountryCode),
      initialState: store.getState(),
      games,
    },
  }
}, 'accountSSR')
